import { ReactElement } from 'react';
import {
  setAuthorizedRepresentatives,
  setBeneficialOwners,
  setCompanySearchResult,
} from '../redux/leaseApp/lease-app-slice';
import { routes } from '../routes';
import { Company } from '../types/instant-lease-api';
import { getPartnerText, getText } from '../utils/getter';
import ConfirmCompanyData from '../view/b2b/components/confirm-company-data/confirm-company-data';
import CmTrRegistry from '../view/b2b/pages/cm-tr-registry/cm-tr-registry';
import CompanyAddress from '../view/b2b/pages/company-address/company-address';
import CompanyContacts from '../view/b2b/pages/company-contacts/company-contacts';
import CompanyData from '../view/b2b/pages/company-data/company-data';
import CompanyInfo from '../view/b2b/pages/company-info/company-info';
import CompanySearchResult from '../view/b2b/pages/company-search-result/company-search-result';
import ConfirmKYC from '../view/b2b/pages/confirm-kyc/confirm-kyc';
import Contract from '../view/b2b/pages/contract/contract';
import CreditCheckResult from '../view/b2b/pages/credit-check-result/credit-check-result';
import FlexFinal from '../view/b2b/pages/flex-final/flex-final';
import HasBeneficialOwners from '../view/b2b/pages/has-beneficial-owners/has-benificial-owners';
import IdNow from '../view/b2b/pages/id-now/id-now';
import SignerData from '../view/b2b/pages/signer-data/signer-data';
import BeneficialOwners from '../view/b2b/templates/beneficial-owners/beneficial-owners';
import AuthorizedRepresentatives from '../view/b2b/templates/company-representatives/authorized-representatives';
import Creditcheck from '../view/b2b/templates/credit-check/credit-check';
import SignersListAGB from '../view/b2b/templates/signers-list-agb/signers-list-agb';
import SignersList from '../view/b2b/templates/signers-list/signers-list';
import DealerSelection from '../view/common/dealer-selection/dealer-selection';
import PostidentIntro from '../view/common/postident-intro/postident-intro';
import Postident from '../view/common/postident/postident';

type RouteDef = {
  key: string;
  route: (leaseAppId: string, phase: string) => string;
  element: ReactElement;
};

const CompanyRoutes: RouteDef[] = [
  {
    key: 'companyInfo',
    route: routes.leaseApplication.companyProfile.companyInfo,
    element: <CompanyInfo createAction={setCompanySearchResult} />,
  },
  {
    key: 'dealerSelection',
    route: routes.leaseApplication.companyProfile.dealerSelection,
    element: <DealerSelection />,
  },
  {
    key: 'companySearchResult',
    route: routes.leaseApplication.companyProfile.companySearchResult,
    element: <CompanySearchResult />,
  },
  {
    key: 'companyAddress',
    route: routes.leaseApplication.companyProfile.companyAddress,
    element: (
      <CompanyAddress
        getLegalform={(leaseApp) => leaseApp?.customer?.company?.legal_form}
        getCustomer={(activeApplication) =>
          activeApplication?.customer?.company
        }
      />
    ),
  },
  {
    key: 'companyContacts',
    route: routes.leaseApplication.companyProfile.companyContacts,
    element: (
      <CompanyContacts
        getCustomer={(activeApplication) =>
          activeApplication?.customer?.company
        }
      />
    ),
  },
  {
    key: 'companyData',
    route: routes.leaseApplication.companyProfile.companyData,
    element: (
      <CompanyData
        getCustomer={(activeApplication) =>
          activeApplication?.customer?.company
        }
      />
    ),
  },
  {
    key: 'confirmCompany',
    route: routes.leaseApplication.companyProfile.confirm,
    element: (
      <ConfirmCompanyData
        title='Zusammenfassung deiner Daten'
        subTitle='Bitte überprüfe deine Eingaben. Solltest du etwas anpassen wollen, klicke einfach auf „ändern“.'
      />
    ),
  },
  {
    key: 'shareholders',
    route: routes.leaseApplication.kyc.shareholders,
    element: (
      <BeneficialOwners
        getLegalform={(leaseApp) => leaseApp?.customer?.company?.legal_form}
        title={getText('ohg_partners_title')}
        subheadline={getText('ohg_partners_subtitle')}
        emptyListTitle={getText('share_holders_title')}
        BOModalTitle='Gesellschafter hinzufügen'
        createAction={setBeneficialOwners}
        getInitialValue={(activeApplication) =>
          activeApplication?.customer?.company?.beneficial_owners
        }
      />
    ),
  },
  {
    key: 'partners',
    route: routes.leaseApplication.kyc.partners,
    element: (
      <BeneficialOwners
        getLegalform={(leaseApp) => leaseApp?.customer?.company?.legal_form}
        title={getText('partg_partners_title')}
        subheadline={getText('partg_partners_subtitle')}
        emptyListTitle={getText('partner_title')}
        BOModalTitle='Partner hinzufügen'
        createAction={setBeneficialOwners}
        getInitialValue={(activeApplication) =>
          activeApplication?.customer?.company?.beneficial_owners
        }
      />
    ),
  },
  {
    key: 'hasBeneficialOwners',
    route: routes.leaseApplication.kyc.hasBeneficialOwners,
    element: (
      <HasBeneficialOwners
        title={getText('beneficial_owners_initial_question')}
        extraInfo={getText('beneficial_owners_popup_content')}
        getLegalform={(leaseApp) => leaseApp?.customer?.company?.legal_form}
        emptyListTitle={getText('beneficial_owners_collection_headline')}
        subheadline='Gibt es wirtschaftlich berechtigte Personen in deinem Unternehmen?'
        BOModalTitle={getText('beneficial_owners_collection_popup_subtitle')}
        createAction={setBeneficialOwners}
        getInitialValue={(activeApplication) =>
          activeApplication?.customer?.company?.beneficial_owners
        }
      />
    ),
  },
  {
    key: 'authorizedRepresentatives',
    route: routes.leaseApplication.kyc.authorizedRepresentatives,
    element: (
      <AuthorizedRepresentatives
        title={getText('company_representatives_headline')}
        emptyListTitle={getText('company_representatives_headline')}
        ARModalTitle='Vertretungsberechtigten hinzufügen'
        createAction={setAuthorizedRepresentatives}
        getInitialValue={(activeApplication) =>
          activeApplication?.customer?.company?.authorized_representatives
        }
      />
    ),
  },
  {
    key: 'ownerData',
    route: routes.leaseApplication.kyc.ownerData,
    element: (
      <SignerData
        type='company'
        getSigner={(activeApplication) =>
          activeApplication?.customer?.company?.signers
        }
      />
    ),
  },

  {
    key: 'transparencyRegister',
    route: routes.leaseApplication.kyc.transparencyRegister,
    element: (
      <CmTrRegistry
        getInitialValue={(activeApplication) =>
          activeApplication?.customer?.company as Company
        }
      />
    ),
  },

  {
    key: 'confirmKyc',
    route: routes.leaseApplication.kyc.confirm,
    element: <ConfirmKYC />,
  },
  {
    key: 'creditCheckStart',
    route: routes.leaseApplication.creditCheck.creditCheckStart,
    element: (
      <Creditcheck
        type='capital'
        getLegalform={(leaseApp) => leaseApp?.customer?.company?.legal_form}
        extraInfo={getPartnerText('creditreform_text')}
      />
    ),
  },
  {
    key: 'creditCheckResult',
    route: routes.leaseApplication.creditCheck.creditCheckStatus,
    element: <CreditCheckResult />,
  },
  {
    key: 'flexFinal',
    route: routes.leaseApplication.flex.flexFinal,
    element: <FlexFinal />,
  },
  {
    key: 'reviewContract',
    route: routes.leaseApplication.identityCheck.reviewContract,
    element: <Contract />,
  },

  {
    key: 'idNow',
    route: routes.leaseApplication.identityCheck.idNow,
    element: <IdNow />,
  },

  {
    key: 'signersListAGB',
    route: routes.leaseApplication.identityCheck.signersListAGB,
    element: <SignersListAGB />,
  },
  {
    key: 'postIdenetIntro',
    route: routes.leaseApplication.identityCheck.postidentStart,
    element: <PostidentIntro />,
  },
  {
    key: 'postIdenetCoupon',
    route: routes.leaseApplication.identityCheck.postidentCoupon,
    element: <Postident />,
  },
  {
    key: 'signersList',
    route: routes.leaseApplication.identityCheck.signersList,
    element: <SignersList />,
  },
];

export default CompanyRoutes;
